import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Cart drawer upsell toggle.
 *
 * Adds or removes a single accessory line, then re-renders the drawer through the theme's
 * own section renderer so the Discount row and Total stay correct without a second source
 * of truth. Capped at one unit: the toggle is a yes/no, not a quantity.
 */
class EbCartUpsell extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    if (!this.input) return;
    this.variantId = this.dataset.variantId;
    this.sectionId = this.dataset.sectionId || 'cart-drawer-section';
    this.input.addEventListener('change', this.onToggle);
    // Tells the CSS the control is live. Without the module the tile stays inert and
    // dimmed rather than toggling into a state the cart never reaches.
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.onToggle);
  }

  onToggle = async (event) => {
    const wantsIt = event.target.checked;
    this.setBusy(true);

    // /cart/add cannot remove, and /cart/change addressed by id cannot add, so the two
    // directions genuinely need different endpoints.
    const url = wantsIt ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const body = wantsIt
      ? { items: [{ id: Number(this.variantId), quantity: 1 }] }
      : { id: String(this.variantId), quantity: 0 };

    body.sections = this.sectionId;
    body.sections_url = window.location.pathname;

    try {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();

      if (data.status || data.errors) {
        throw new Error(data.description || data.message || 'Cart update failed');
      }

      const html = data.sections?.[this.sectionId];
      if (html) await morphSection(this.sectionId, html, { mode: 'hydration' });

      // The morph replaces this element, so nothing past this point may touch `this`.
      // Confirmation lands on the line that actually appeared rather than on the tile,
      // which no longer exists by now. Line keys are `<variantId>:<hash>`.
      if (wantsIt) EbCartUpsell.flagNewLine(this.variantId);
    } catch (error) {
      // Put the toggle back where the cart actually is, rather than leaving it lying.
      event.target.checked = !wantsIt;
      this.setBusy(false);
      console.error('[eb-cart-upsell]', error);
    }
  };

  /**
   * Briefly highlights a freshly added cart line.
   * @param {string} variantId
   */
  static flagNewLine(variantId) {
    requestAnimationFrame(() => {
      const row = document.querySelector(`[data-key^="${variantId}:"]`);
      if (!row) return;
      row.classList.add('eb-just-added');
      row.addEventListener('animationend', () => row.classList.remove('eb-just-added'), { once: true });
    });
  }

  setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
    if (this.input) this.input.disabled = busy;
  }
}

if (!customElements.get('eb-cart-upsell')) {
  customElements.define('eb-cart-upsell', EbCartUpsell);
}
